import React from 'react';
import {
    Box,
    Divider,
    IconButton,
    LinearProgress,
    TextField,
    Typography
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Close as CloseIcon,
    Image as ImageIcon,
    Videocam as VideoIcon,
    PhotoCamera as CameraIcon,
    CheckCircle as SuccessIcon
} from '@mui/icons-material';
import { FileUploader } from 'react-drag-drop-files';
import VideoThumbnailExtractor from '../../guides/VideoThumbnailExtractor';
import { isValidVideoUrl, isValidYouTubeUrl, getYouTubeThumbnailUrl, getVideoTooLargeMessage } from '../../../utils/videoUtils';
import { useMaxVideoUploadMb } from '../../../hooks/useMaxVideoUploadMb';

/**
 * MediaSection - Images and video upload/URL handling
 * Modern redesign with unified upload progress at bottom
 */
const MediaSection = ({
    // Image props
    imagePreview,
    coverPreview,
    uploadStates,
    handleImageUpload,

    // Video props
    isVideoUploadMode,
    setIsVideoUploadMode,
    videoFile,
    videoFileName,
    videoPreviewUrl,
    existingVideoUrl,
    handleVideoFileChange,
    handleVideoUrlChange,
    handleRemoveVideo,
    handleCoverExtracted,
    formValues,
    setFieldValue,

    // YouTube thumbnail handler
    onYouTubeThumbnailExtracted
}) => {
    const maxVideoMb = useMaxVideoUploadMb();
    const isImageUploading = uploadStates.image.phase === 'uploading' || uploadStates.image.phase === 'compressing';
    const isCoverUploading = uploadStates.cover.phase === 'uploading' || uploadStates.cover.phase === 'compressing';
    const isVideoUploading = uploadStates.video.phase === 'uploading' || uploadStates.video.phase === 'compressing';

    // Check if any upload is active
    const hasActiveUpload = isImageUploading || isCoverUploading || isVideoUploading;

    // Get current active upload info
    const getActiveUploadInfo = () => {
        if (isImageUploading) {
            return {
                label: 'תמונה ראשית',
                phase: uploadStates.image.phase,
                progress: uploadStates.image.phase === 'uploading'
                    ? uploadStates.image.uploadProgress
                    : uploadStates.image.compressionProgress
            };
        }
        if (isCoverUploading) {
            return {
                label: 'תמונת כיסוי',
                phase: uploadStates.cover.phase,
                progress: uploadStates.cover.phase === 'uploading'
                    ? uploadStates.cover.uploadProgress
                    : uploadStates.cover.compressionProgress
            };
        }
        if (isVideoUploading) {
            return {
                label: 'סרטון',
                phase: uploadStates.video.phase,
                progress: uploadStates.video.phase === 'uploading'
                    ? uploadStates.video.uploadProgress
                    : uploadStates.video.compressionProgress
            };
        }
        return null;
    };

    // Handle file input for images
    const handleFileInput = async (e, type) => {
        const file = e.target.files[0];
        if (file) {
            await handleImageUpload(file, type);
        }
    };

    // Handle video URL input with YouTube thumbnail extraction
    const handleVideoUrlInput = (e) => {
        const url = e.target.value;
        handleVideoUrlChange(url);

        // Auto-extract YouTube thumbnail
        if (isValidYouTubeUrl(url) && onYouTubeThumbnailExtracted) {
            const thumbnailUrl = getYouTubeThumbnailUrl(url);
            if (thumbnailUrl) {
                onYouTubeThumbnailExtracted(thumbnailUrl);
            }
        }
    };

    // Compact image upload card
    const renderImageCard = (title, preview, type, isSquare = false) => {
        const uploadState = type === 'image' ? uploadStates.image : uploadStates.cover;
        const isUploading = uploadState.phase === 'uploading' || uploadState.phase === 'compressing';
        const isComplete = uploadState.phase === 'complete';

        return (
            <Box sx={{ position: 'relative' }}>
                <Typography
                    sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#475569',
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    {isUploading && (
                        <Box
                            component="span"
                            sx={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#F59E0B',
                                animation: 'pulse 1.5s infinite'
                            }}
                        />
                    )}
                    {isComplete && <SuccessIcon sx={{ fontSize: '14px', color: '#22C55E' }} />}
                    {title}
                </Typography>

                <Box
                    component="label"
                    sx={{
                        display: 'block',
                        width: isSquare ? '160px' : '100%',
                        height: isSquare ? '160px' : '120px',
                        borderRadius: '10px',
                        border: preview ? '2px solid #E2E8F0' : '2px dashed #CBD5E1',
                        backgroundColor: preview ? 'transparent' : '#F8FAFC',
                        overflow: 'hidden',
                        cursor: isUploading ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        '&:hover': {
                            borderColor: isUploading ? '#E2E8F0' : '#57837E',
                            transform: isUploading ? 'none' : 'translateY(-2px)',
                            boxShadow: isUploading ? 'none' : '0 4px 12px rgba(0,0,0,0.08)'
                        }
                    }}
                >
                    <input
                        accept="image/png, image/gif, image/jpeg"
                        onChange={(e) => handleFileInput(e, type)}
                        type="file"
                        hidden
                        disabled={isUploading}
                    />

                    {preview ? (
                        <>
                            <img
                                src={preview}
                                alt={title}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                            {!isUploading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(0,0,0,0.4)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0,
                                        transition: 'opacity 0.2s ease',
                                        '&:hover': { opacity: 1 }
                                    }}
                                >
                                    <CameraIcon sx={{ color: 'white', fontSize: '28px' }} />
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5
                            }}
                        >
                            <UploadIcon sx={{ fontSize: '32px', color: '#94A3B8' }} />
                            <Typography sx={{ fontSize: '12px', color: '#64748B' }}>
                                העלאה
                            </Typography>
                        </Box>
                    )}

                    {/* Upload overlay */}
                    {isUploading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255,255,255,0.85)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Box
                                sx={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    border: '3px solid #E2E8F0',
                                    borderTopColor: '#57837E',
                                    animation: 'spin 1s linear infinite'
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const activeUpload = getActiveUploadInfo();

    return (
        <Box>
            {/* Section Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <ImageIcon sx={{ color: '#57837E', fontSize: '22px' }} />
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: '16px',
                        color: '#1E293B',
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}
                >
                    מדיה
                </Typography>
            </Box>

            {/* Images Row */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                {/* Main Image - Square */}
                <Box sx={{ flex: '0 0 auto' }}>
                    {renderImageCard('תמונה ראשית', imagePreview, 'image', true)}
                </Box>

                {/* Cover Image - Wide */}
                <Box sx={{ flex: 1, minWidth: '250px', maxWidth: '400px' }}>
                    {renderImageCard('תמונת כיסוי לסרטון', coverPreview, 'cover', false)}
                </Box>
            </Box>

            <Divider sx={{ my: 2.5, borderColor: '#E2E8F0' }} />

            {/* Video Section */}
            <Box>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                }}>
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#475569',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        <VideoIcon sx={{ fontSize: '18px', color: '#57837E' }} />
                        סרטון הדגמה
                    </Typography>

                    {/* Video Mode Toggle */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#F1F5F9',
                            borderRadius: '20px',
                            padding: '4px'
                        }}
                    >
                        <Box
                            onClick={() => !isVideoUploading && setIsVideoUploadMode(false)}
                            sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: '16px',
                                cursor: isVideoUploading ? 'default' : 'pointer',
                                backgroundColor: !isVideoUploadMode ? '#FFFFFF' : 'transparent',
                                boxShadow: !isVideoUploadMode ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <Typography sx={{
                                fontSize: '12px',
                                fontWeight: !isVideoUploadMode ? 600 : 400,
                                color: !isVideoUploadMode ? '#1E293B' : '#64748B'
                            }}>
                                קישור
                            </Typography>
                        </Box>
                        <Box
                            onClick={() => !isVideoUploading && setIsVideoUploadMode(true)}
                            sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: '16px',
                                cursor: isVideoUploading ? 'default' : 'pointer',
                                backgroundColor: isVideoUploadMode ? '#FFFFFF' : 'transparent',
                                boxShadow: isVideoUploadMode ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <Typography sx={{
                                fontSize: '12px',
                                fontWeight: isVideoUploadMode ? 600 : 400,
                                color: isVideoUploadMode ? '#1E293B' : '#64748B'
                            }}>
                                העלאה
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {!isVideoUploadMode ? (
                    // Video URL input
                    <TextField
                        fullWidth
                        placeholder="https://youtube.com/watch?v=... או קישור ישיר לסרטון"
                        value={formValues.videoUrl || ''}
                        onChange={handleVideoUrlInput}
                        error={formValues.videoUrl?.trim().length > 0 && !isValidVideoUrl(formValues.videoUrl)}
                        helperText={
                            formValues.videoUrl?.trim().length > 0 && !isValidVideoUrl(formValues.videoUrl)
                                ? 'הזן קישור תקין לסרטון'
                                : 'תומך ביוטיוב וקישורים ישירים לסרטונים'
                        }
                        FormHelperTextProps={{ sx: { textAlign: 'right', color: '#94A3B8' } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#FFFFFF',
                                borderRadius: '10px',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#57837E' }
                            }
                        }}
                    />
                ) : (
                    // Video upload
                    <Box>
                        {(videoFile || existingVideoUrl) ? (
                            <Box
                                sx={{
                                    borderRadius: '12px',
                                    border: '1px solid #E2E8F0',
                                    overflow: 'hidden',
                                    backgroundColor: '#FAFAFA'
                                }}
                            >
                                {/* Video filename header */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px 16px',
                                    backgroundColor: '#F8FAFC',
                                    borderBottom: '1px solid #E2E8F0'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '8px',
                                                backgroundColor: '#57837E',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <VideoIcon sx={{ color: 'white', fontSize: '18px' }} />
                                        </Box>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1E293B' }}>
                                            {videoFileName}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={handleRemoveVideo}
                                        disabled={isVideoUploading}
                                        sx={{
                                            color: '#94A3B8',
                                            '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' }
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                {/* Video Preview Player */}
                                {videoPreviewUrl && (
                                    <Box sx={{ backgroundColor: '#000' }}>
                                        <video
                                            controls
                                            style={{
                                                width: '100%',
                                                maxHeight: '280px',
                                                display: 'block'
                                            }}
                                            src={videoPreviewUrl}
                                        >
                                            הדפדפן שלך לא תומך בתגית וידאו.
                                        </video>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <FileUploader
                                handleChange={handleVideoFileChange}
                                name="video"
                                types={['MP4', 'WEBM', 'MOV', 'AVI']}
                                maxSize={maxVideoMb}
                                onTypeError={() => alert('אנא בחר קובץ וידאו בפורמט נתמך (MP4, WEBM, MOV, AVI)')}
                                onSizeError={() => alert(getVideoTooLargeMessage(maxVideoMb))}
                            >
                                <Box
                                    sx={{
                                        border: '2px dashed #CBD5E1',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        backgroundColor: '#F8FAFC',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            borderColor: '#57837E',
                                            backgroundColor: '#F0FDF9'
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        padding: '40px 20px',
                                        textAlign: 'center'
                                    }}>
                                        <Box
                                            sx={{
                                                width: '56px',
                                                height: '56px',
                                                borderRadius: '50%',
                                                backgroundColor: '#E2E8F0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto',
                                                mb: 2
                                            }}
                                        >
                                            <VideoIcon sx={{ fontSize: '28px', color: '#64748B' }} />
                                        </Box>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1E293B', mb: 0.5 }}>
                                            גררו ושחררו קובץ וידאו כאן
                                        </Typography>
                                        <Typography sx={{ fontSize: '13px', color: '#64748B', mb: 2 }}>
                                            או לחצו לבחירת קובץ
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#94A3B8' }}>
                                            MP4, WEBM, MOV, AVI - עד {maxVideoMb}MB
                                        </Typography>
                                    </Box>
                                </Box>
                            </FileUploader>
                        )}

                        {/* Video Thumbnail Extraction */}
                        {!isVideoUploading && (videoFile || existingVideoUrl) && (
                            <Box sx={{ mt: 2 }}>
                                <VideoThumbnailExtractor
                                    key={videoFile ? videoFile.name + videoFile.size : existingVideoUrl || 'no-video'}
                                    videoFile={videoFile}
                                    videoUrl={existingVideoUrl}
                                    onThumbnailChange={handleCoverExtracted}
                                    thumbnailPreview={coverPreview}
                                    disabled={false}
                                    initialTime={0}
                                    showManualUpload={true}
                                    aspectRatio="landscape"
                                />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Bottom Upload Progress Bar */}
            {hasActiveUpload && activeUpload && (
                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        backgroundColor: '#F0FDF9',
                        borderRadius: '10px',
                        border: '1px solid #A7F3D0'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#065F46' }}>
                            {activeUpload.phase === 'uploading' ? 'מעלה' : 'מעבד'} {activeUpload.label}...
                        </Typography>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#059669' }}>
                            {Math.round(activeUpload.progress)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={activeUpload.progress}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#D1FAE5',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                backgroundColor: '#10B981'
                            }
                        }}
                    />
                </Box>
            )}

            {/* CSS Animations */}
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                `}
            </style>
        </Box>
    );
};

export default MediaSection;
